import { useTranslation } from "react-i18next";
import { VonState } from "./VennootschapOfNietPage";
import { calculateSoleProprietorshipTaxes } from "../../logic/vennootschapofniet/soleproprietorshiptaxes";
import { calculateCorporationTaxes } from "../../logic/vennootschapofniet/corporatetaxes";
import { VonGraph } from "../../components/VonGraph"
import { Detail } from "../../components/Detail"
import { useState } from "react";

const DEFAULT_PENSION_AGE = 67

function yearsTillPension(dateOfBirth: Date, pensionAge: number): number {
    const thisYear = new Date().getFullYear()
    const yearOfPension = dateOfBirth.getFullYear() + pensionAge
    return yearOfPension - thisYear
}

function isValid(input: Required<VonState>): boolean {
    if (input.allowance < 0) return false
    if (input.dependents < 0) return false
    if (input.expenses < 0) return false
    if (input.turnOver < 0) return false
    if (input.turnOver < input.allowance + input.expenses) return false
    return true
}

export function ResultPage({ input, goToIntroPage }: { input: Required<VonState>, goToIntroPage: () => void }) {
    const { t } = useTranslation(["vennootschapofniet", "common"])

    const [activeGraph, setActiveGraph] = useState('total')

    if (!isValid(input)) {
        return (
            <>
                <h1>{t("result.invalid_input")}</h1>
                <div className="wrapper-row">
                    <button className="button-primary">{t("button__finish", { ns: "common" })}</button>
                    <button className="button-secondary">{t("button__info", { ns: "common" })}</button>
                    <button className="button-void" onClick={goToIntroPage}>{t("button__retry", { ns: "common" })}</button>
                </div>
            </>)
    }

    const pensionAge = DEFAULT_PENSION_AGE
    const soleProprietorshipCosts = calculateSoleProprietorshipTaxes(input)
    const corporationCosts = calculateCorporationTaxes(input)
    const corporationPreferred = soleProprietorshipCosts.total() > corporationCosts.total()
    const difference = Math.abs(soleProprietorshipCosts.total() - corporationCosts.total())
    const yearlyAmount = difference.toLocaleString("be-NL", {
        style: "currency",
        currency: "EUR",
    })
    const lifetimeAmount = (difference * yearsTillPension(input.dateOfBirth, pensionAge)).toLocaleString("be-NL", {
        style: "currency",
        currency: "EUR",
    })

    return (
        <div>
            <h1 className="scroll-m-20 text-3xl font-extrabold tracking-tight ">
                {corporationPreferred ? t("result.title_corporation") : t("result.title_emz")}
            </h1>
            <div className="flex flex-col mt-8">
                <p className="text-lg text-muted-foreground leading-7 [&:not(:first-child)]:mt-6">
                    {corporationPreferred
                        ? t("result.yearly_tax_decreases", { yearlyAmount: yearlyAmount, pensionAge: pensionAge, lifetimeAmount: lifetimeAmount })
                        : t("result.yearly_tax_increases", { yearlyAmount: yearlyAmount, pensionAge: pensionAge, lifetimeAmount: lifetimeAmount })}
                </p>

                <br></br>
            </div>

            <div className="tablist">
                <span className={activeGraph === 'socialContribution' ? 'active' : ''} onClick={() => setActiveGraph('socialContribution')}>{t("result.social_contribution")}</span>
                <span className={activeGraph === 'personalIncomeTax' ? 'active' : ''} onClick={() => setActiveGraph('personalIncomeTax')}>{t("result.personal_income_tax")}</span>
                <span className={activeGraph === 'corporationAndWithholdingTax' ? 'active' : ''} onClick={() => setActiveGraph('corporationAndWithholdingTax')}>{t("result.corporation_and_withholding_tax")}</span>
                <span className={activeGraph === 'administration' ? 'active' : ''} onClick={() => setActiveGraph('administration')}>{t("result.administartion")}</span>
                <span className={activeGraph === 'total' ? 'active' : ''} onClick={() => setActiveGraph('total')}>{t("result.total")}</span>
            </div>

            <div>
                {activeGraph === 'socialContribution' &&
                    <>
                        <p>{t("result.social_contribution_estimation")}</p>
                        <VonGraph
                            soleProprietorshipCost={soleProprietorshipCosts.socialContribution}
                            corporationCost={corporationCosts.socialContribution}
                            title_venn={t("result.corporation")}
                            title_emz={t("result.one_man_army")}
                        />
                    </>
                }
                {activeGraph === 'personalIncomeTax' &&
                    <>
                        <p>{t("result.personal_income_tax_estimation")}</p>
                        <VonGraph
                            soleProprietorshipCost={soleProprietorshipCosts.personalIncomeTax}
                            corporationCost={corporationCosts.personalIncomeTax}
                            title_venn={t("result.corporation")}
                            title_emz={t("result.one_man_army")}
                        />
                    </>
                }
                {activeGraph === 'corporationAndWithholdingTax' &&
                    <>
                        <p>{t("result.corporation_and_withholding_tax_estimation")}</p>
                        <VonGraph
                            soleProprietorshipCost={soleProprietorshipCosts.corporationAndWithholdingTax}
                            corporationCost={corporationCosts.corporationAndWithholdingTax}
                            title_venn={t("result.corporation")}
                            title_emz={t("result.one_man_army")}
                        />
                    </>
                }
                {activeGraph === 'administration' &&
                    <>
                        <p>{t("result.administartion_estimation")}</p>
                        <VonGraph
                            soleProprietorshipCost={soleProprietorshipCosts.administration}
                            corporationCost={corporationCosts.administration}
                            title_venn={t("result.corporation")}
                            title_emz={t("result.one_man_army")}
                        />
                    </>
                }
                {activeGraph === 'total' &&
                    <>
                        <p>{t("result.total_estimation")}</p>
                        <VonGraph
                            soleProprietorshipCost={soleProprietorshipCosts.total()}
                            corporationCost={corporationCosts.total()}
                            title_venn={t("result.corporation")}
                            title_emz={t("result.one_man_army")}
                        />
                    </>
                }

                <br></br>

            </div>

            <Detail
                descriptionHTML={`            
                    <p className="leading-7 [&:not(:first-child)]:mt-6 font-bold">
                        ${t("result.simulation_assumptions.title")}
                    </p>
                    <ul className="list-disc list-inside	">
                        <li>${t("result.simulation_assumptions.optimal_payout")}</li>
                        <li>${t("result.simulation_assumptions.small_company")}</li>
                        <li>${t("result.simulation_assumptions.taxes")}</li>
                    </ul>

                    <p className="leading-7 [&:not(:first-child)]:mt-6 font-bold">
                        ${t("result.not_accounted_for.title")}
                    </p>
                    <ul className="list-disc list-inside	">
                        <li>${t("result.not_accounted_for.extralegal")}</li>
                        <li>${t("result.not_accounted_for.pension_savings")}</li>
                        <li>${t("result.not_accounted_for.renting_office")}</li>
                        <li>${t("result.not_accounted_for.royalty")}</li>
                        <li>${t("result.not_accounted_for.commuting")}</li>
                        <li>${t("result.not_accounted_for.stocks")}</li>
                    </ul>
                `}

            />

            <div className="wrapper-row">
                <button className="button-primary" onClick={() => window.open(process.env.REACT_APP_GLIDE_URL)}>{t("button__finish", { ns: "common" })}</button>
                {/* <button className="button-secondary">{t("button__info", { ns: "common" })}</button> */}
                <button className="button-void" onClick={goToIntroPage}>{t("button__retry", { ns: "common" })}</button>
            </div>
        </div>
    )
}